import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const WallOfDominance = ({ images, title, subtitle, glowColor = "blue" }) => {
  const [selectedIndex, setSelectedIndex] = useState(null);

  const defaultImages = [
    "https://media-manager-c.questera.ai/greta-media/22508bb601940b4e20f7d160303c50efd71c7583e6bf992668d180d798243f38c2166642d2b71e0a7892275161f0f512/images/aW1hZ2UvanBlZw==/948606d1b2549e977908f352c965185b.jpg",
    "https://media-manager-c.questera.ai/greta-media/22508bb601940b4e20f7d160303c50efd71c7583e6bf992668d180d798243f38c2166642d2b71e0a7892275161f0f512/images/aW1hZ2UvanBlZw==/561ac44d52ff94b74600a194812b0940.jpg",
    "https://media-manager-c.questera.ai/greta-media/22508bb601940b4e20f7d160303c50efd71c7583e6bf992668d180d798243f38c2166642d2b71e0a7892275161f0f512/images/aW1hZ2UvanBlZw==/815271d41ca05cf0fc96b4f1dd28a9d1.jpg"
  ];

  const displayImages = images || defaultImages;
  
  const handlePrev = (e) => {
    e.stopPropagation();
    setSelectedIndex((prev) => (prev === 0 ? displayImages.length - 1 : prev - 1));
  };

  const handleNext = (e) => {
    e.stopPropagation();
    setSelectedIndex((prev) => (prev === displayImages.length - 1 ? 0 : prev + 1));
  };

  const cards = displayImages.map((src, i) => {
    const isFive = displayImages.length === 5;
    const offset = i - Math.floor(displayImages.length / 2);
    
    // Exact positioning for the 5-card layout requested
    if (isFive) {
      const positions = [
        { rotate: -20, x: -360, y: 120, z: 10 },
        { rotate: -10, x: -180, y: 50, z: 20 },
        { rotate: 0, x: 0, y: 0, z: 30 },
        { rotate: 10, x: 180, y: 50, z: 20 },
        { rotate: 20, x: 360, y: 120, z: 10 }
      ];
      return { id: i, src, ...positions[i] };
    }
    
    return {
      id: i,
      src,
      rotate: offset * 8,
      x: offset * 140,
      y: Math.abs(offset) * 20,
      z: 10 + i
    };
  });

  const glowClasses = {
    blue: "bg-blue-600/5",
    orange: "bg-orange-600/5",
    red: "bg-red-600/5"
  };

  return (
    <section className="pt-24 pb-12 bg-[#0B0F19] overflow-hidden border-b border-white/5 relative">
      <div className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] ${glowClasses[glowColor] || glowClasses.blue} blur-[150px] rounded-full pointer-events-none`}></div>
      
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div className="text-center mb-12 md:mb-20">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 tracking-tight">{title || "Wall of Dominance"}</h2>
          <p className="text-lg text-slate-400 font-medium max-w-2xl mx-auto">{subtitle || "Real results. Real visibility."}</p>
        </div>

        <div className="relative w-full h-[300px] sm:h-[400px] md:h-[550px] flex justify-center items-start mt-6 perspective-[1000px] transform scale-[0.45] sm:scale-[0.65] md:scale-100 origin-top">
          {cards.map((card, idx) => (
            <motion.div
              key={card.id}
              onClick={() => setSelectedIndex(idx)}
              className="absolute top-0 w-[320px] rounded-[1.5rem] shadow-[0_30px_60px_rgba(0,0,0,0.6)] border border-white/10 bg-white overflow-hidden cursor-zoom-in"
              style={{ zIndex: card.z }}
              initial={{ opacity: 0, y: 300, x: card.x, rotate: card.rotate }}
              whileInView={{ opacity: 1, y: card.y, x: card.x, rotate: card.rotate }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ type: "spring", stiffness: 100, damping: 15, delay: idx * 0.1 }}
              whileHover={{
                y: card.y - 80,
                rotate: 0,
                scale: 1.15,
                zIndex: 100,
                transition: { type: "spring", stiffness: 300, damping: 20 }
              }}
            >
              <img src={card.src} alt={`Result ${idx + 1}`} className="w-full h-auto block pointer-events-none" />
              <div className="absolute inset-0 bg-black/0 hover:bg-black/10 transition-colors flex items-center justify-center opacity-0 hover:opacity-100">
                <div className="bg-white/90 p-3 rounded-full text-slate-900 shadow-xl">
                  <SafeIcon icon={FiIcons.FiMaximize2} className="text-xl" />
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {selectedIndex !== null && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedIndex(null)}
            className="fixed inset-0 z-[100] bg-[#0B0F19]/98 backdrop-blur-2xl flex items-center justify-center p-4 md:p-12 cursor-zoom-out"
          >
            {/* Close Button */}
            <button 
              onClick={() => setSelectedIndex(null)}
              className="absolute top-8 right-8 text-white/50 hover:text-white transition-colors z-[110]"
            >
              <SafeIcon icon={FiIcons.FiX} className="text-4xl" />
            </button>

            {/* Navigation Arrows */}
            <button 
              onClick={handlePrev}
              className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 w-16 h-16 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white hover:bg-white/10 hover:scale-110 transition-all z-[110]"
            >
              <SafeIcon icon={FiIcons.FiChevronLeft} className="text-4xl" />
            </button>

            <button 
              onClick={handleNext}
              className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 w-16 h-16 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white hover:bg-white/10 hover:scale-110 transition-all z-[110]"
            >
              <SafeIcon icon={FiIcons.FiChevronRight} className="text-4xl" />
            </button>

            {/* Image Container - Fixed to show full photo */}
            <motion.div 
              key={selectedIndex}
              initial={{ scale: 0.9, opacity: 0, x: 20 }}
              animate={{ scale: 1, opacity: 1, x: 0 }}
              exit={{ scale: 0.9, opacity: 0, x: -20 }}
              className="relative max-w-full max-h-[85vh] flex items-center justify-center"
              onClick={(e) => e.stopPropagation()}
            >
              <img 
                src={displayImages[selectedIndex]} 
                alt="Detailed Result" 
                className="max-w-full max-h-[85vh] object-contain rounded-xl shadow-[0_0_100px_rgba(0,0,0,0.8)] border border-white/10" 
              />
              <div className="absolute -bottom-10 left-1/2 -translate-x-1/2 text-white/40 font-bold tracking-widest text-sm">
                {selectedIndex + 1} / {displayImages.length}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default WallOfDominance;